import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    Injector,
    input,
    OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { tap } from 'rxjs';
import { BudgetFormGroup } from './models/budget.model';
import { NumberInputComponent } from '../number-input';
import { SkipTabDirective } from '../../../directives/skip-tab.directive';

@Component({
    selector: 'tran-budgets',
    imports: [ReactiveFormsModule, NumberInputComponent, TranslateModule, SkipTabDirective],
    templateUrl: './budgets.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BudgetsComponent implements OnInit {
    public readonly submitted = input(false);
    public readonly initialYears = input(3);
    public readonly control = input.required<FormArray<BudgetFormGroup>>();

    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    private readonly elementRef = inject(ElementRef<HTMLElement>);

    protected readonly required = computed(() => this.control().hasValidator(Validators.required));

    protected readonly totalControl = new FormControl<number>({ value: 0, disabled: false }, { nonNullable: true });

    public ngOnInit(): void {
        if (this.control().length === 0) {
            this.initDefaultRows();
        }

        this.handleTotal();
        this.updateTotal();
    }

    protected addYear(): void {
        const next = this.nextFiscalYear();

        this.control().push(this.createRow(next, null));
        this.control().markAsTouched();

        this.handleFocus('last');
    }

    protected removeYear(index: number): void {
        this.control().removeAt(index);
        this.updateTotal();
        this.handleFocus('first');
    }

    protected get total(): number {
        // Fix floating-point precision issue: 10.99 + 11.25 would produce 22.240000000000002
        // Round to 2 decimal places (suitable for currency calculations)
        const sum = this.control().controls.reduce((sum, row) => sum + (row.controls.amount.value ?? 0), 0);
        return Math.round(sum * 100) / 100;
    }

    private handleFocus(focusElement: 'first' | 'last'): void {
        afterNextRender(
            () => {
                const inputs = this.elementRef.nativeElement.querySelectorAll('tran-number-input input[id^="amount-"]');
                if (inputs.length > 0) {
                    if (focusElement === 'first') {
                        inputs[0]?.focus();
                    } else {
                        inputs[inputs.length - 1]?.focus();
                    }
                }
            },
            { injector: this.injector }
        );
    }

    private handleTotal(): void {
        this.control()
            .valueChanges.pipe(
                tap(() => this.updateTotal()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private updateTotal(): void {
        this.totalControl.setValue(this.total, { emitEvent: false });
    }

    private initDefaultRows(): void {
        const start = this.displayFiscalYear();
        this.control().clear({ emitEvent: false });

        for (let i = 0; i < this.initialYears(); i++) {
            this.control().push(this.createRow(start + i, null), { emitEvent: false });
        }

        this.updateTotal();
    }

    private createRow(fiscalYear: number, amount: number | null): BudgetFormGroup {
        return new FormGroup({
            id: new FormControl<string | null>(null),
            fiscalYear: new FormControl(fiscalYear, { nonNullable: true }),
            amount: new FormControl<number | null>(amount, {
                validators: [Validators.required, Validators.min(0), Validators.max(999999999.99)]
            })
        });
    }

    private nextFiscalYear(): number {
        const fiscalYears = this.control().controls.map(r => r.controls.fiscalYear.value);
        const maxYear = fiscalYears.length ? Math.max(...fiscalYears) : this.displayFiscalYear();
        return maxYear + 1;
    }

    private displayFiscalYear(): number {
        const today = new Date();
        const fiscalYearDisplayRolloverMonth = 7;

        return today.getMonth() < fiscalYearDisplayRolloverMonth ? today.getFullYear() : today.getFullYear() + 1;
    }
}
